import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, ExternalLink } from 'lucide-react';

export const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tawkLoaded, setTawkLoaded] = useState(false);
  
  // CONFIGURATION - Update these values with your own
  const TAWK_PROPERTY_ID = null; // Set to your Tawk.to Property ID, e.g., '5f8d8e8e4704467e89f0c3e9'
  const WHATSAPP_NUMBER = '15042367479'; // Your WhatsApp Business number
  const CONTACT_EMAIL = 'therealsambaman@yahoo.com'; // Fallback contact email

  useEffect(() => {
    // Only load Tawk.to if a valid Property ID is configured
    if (TAWK_PROPERTY_ID && TAWK_PROPERTY_ID !== 'YOUR_PROPERTY_ID') {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/default`;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      
      script.onload = () => setTawkLoaded(true);
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);

      return () => {
        const tawkWidget = document.getElementById('tawk-bubble');
        if (tawkWidget) tawkWidget.remove();
      };
    }
  }, []);

  // If Tawk.to is loaded, don't show our custom button (Tawk has its own)
  if (tawkLoaded) return null;

  const handleWhatsApp = () => {
    if (WHATSAPP_NUMBER) {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=Hi! I have a question about Escola de Samba Casa Samba.`, '_blank');
    }
  };

  const handleEmail = () => {
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=Question about Escola de Samba Casa Samba`;
  };

  return (
    <>
      {/* Chat Button - positioned above the Emergent badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-6 z-50 bg-amber-500 hover:bg-amber-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        data-testid="live-chat-button"
        aria-label="Open chat"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-36 right-6 z-50 w-80 bg-stone-900 border border-stone-700 rounded-2xl shadow-2xl overflow-hidden"
          data-testid="live-chat-panel"
        >
          {/* Header */}
          <div className="bg-amber-500 px-4 py-3">
            <h3 className="text-white font-bold text-lg">Chat with Us</h3>
            <p className="text-amber-100 text-sm">We typically reply within a few hours</p>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* WhatsApp Option */}
            {WHATSAPP_NUMBER ? (
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center gap-3 p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                data-testid="whatsapp-chat-button"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-medium">Chat on WhatsApp</span>
                <ExternalLink className="w-4 h-4 ml-auto" />
              </button>
            ) : null}

            {/* Email Option */}
            <button
              onClick={handleEmail}
              className="w-full flex items-center gap-3 p-3 bg-stone-700 hover:bg-stone-600 text-white rounded-lg transition-colors"
              data-testid="email-chat-button"
            >
              <Send className="w-5 h-5" />
              <span className="font-medium">Send us an Email</span>
              <ExternalLink className="w-4 h-4 ml-auto" />
            </button>

            {/* Info Text */}
            <p className="text-stone-400 text-xs text-center pt-2">
              Have questions about our music or need help with your order? We're here to help!
            </p>
          </div>

          {/* Setup Notice for Admin */}
          {!WHATSAPP_NUMBER && !TAWK_PROPERTY_ID && (
            <div className="px-4 pb-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-xs">
                  <strong>Admin:</strong> Configure live chat in <code className="bg-stone-800 px-1 rounded">LiveChat.js</code> with your Tawk.to ID or WhatsApp number.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
